// lib/fbclid.js
// Captura y persistencia del click id de Meta (fbclid), y reconstrucción del _fbc.
//
// Al aterrizar desde un ad de Meta la URL trae ?fbclid=. El único que escribe la
// cookie _fbc es fbevents.js del pixel: si el pixel está bloqueado, o si ITP recorta
// a 7 días las cookies escritas por JS, el click id se pierde y Meta ya no puede
// atribuir la compra. La cobertura de fbc en fb_ads_params venía cayendo sola:
// 90,7% en ago-2025 contra 64,9% en jul-2026, o sea un tercio de los checkouts
// llegando sin click id.
//
// Es el mismo problema que gclid.js ya resuelve para Google, y este módulo es su
// calco: cookie propia a 90 días (ventana de conversión por click) más espejo en
// localStorage. El formato del _fbc lo define Meta: fb.{subdominio}.{ts}.{fbclid},
// donde el subdominio se cuenta desde el host (ver subdominioIndex más abajo).

import { getCookie } from "@/utils/cookies";

const OWN_KEY = "bcm_fbc";
const FBC_COOKIE = "_fbc";
const MAX_AGE_DAYS = 90;

function persist(value) {
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${OWN_KEY}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  try {
    window.localStorage.setItem(OWN_KEY, JSON.stringify({ v: value, t: Date.now() }));
  } catch {
    /* localStorage puede estar bloqueado (modo privado) */
  }
}

/**
 * El índice de subdominio que Meta pide en el _fbc: cuenta los saltos del host donde
 * vive la cookie ("com" = 0, "briancallimusic.com" = 1, "www.briancallimusic.com" = 2).
 * Se calcula y no se hardcodea en 1 porque la cookie se escribe sin `domain=`, o sea
 * host-only: el host es el de la página, y el sitio responde por briancallimusic.com
 * y por www. Con el número equivocado Meta puede descartar el click id.
 *
 * Exportado porque el _fbp lleva el mismo índice (ver lib/fbp.js).
 */
export function subdominioIndex() {
  try {
    return Math.max(0, String(window.location.hostname).split(".").length - 1);
  } catch {
    return 1;
  }
}

/** El fbclid que hay adentro de un _fbc ya armado (fb.<n>.<ts>.<fbclid>), o null. */
function fbclidDe(fbc) {
  if (!fbc) return null;
  const parts = String(fbc).split(".");
  return parts.length >= 4 ? parts.slice(3).join(".") : null;
}

/** El timestamp (ms) que lleva adentro un _fbc, o null si el formato no es el de Meta. */
function tsDe(fbc) {
  const parts = String(fbc || "").split(".");
  if (parts.length < 4 || parts[0] !== "fb") return null;
  const ts = Number(parts[2]);
  return Number.isFinite(ts) && ts > 0 ? ts : null;
}

/**
 * Un _fbc sirve mientras el click tenga menos de 90 días.
 *
 * Hace falta chequearlo porque la cookie _fbc que escribe fbevents.js se RENUEVA a 90
 * días en cada visita SIN cambiar el valor: un visitante que volvió después de clickear
 * un aviso hace medio año sigue paseando el click id original, y mandarlo igual es lo
 * que Events Manager marca como "server sending expired fbclid value in fbc parameter"
 * (afecta ViewContent, InitiateCheckout y Purchase). Un fbc vencido no atribuye nada:
 * sólo ensucia la señal. Mismo umbral que MetaCapiService::FBC_MAX_AGE_DAYS en admin-back.
 */
export function fbcVigente(valor, ahora = Date.now()) {
  const ts = tsDe(valor);
  if (ts === null) return false;
  // Reloj del cliente adelantado: se tolera un día antes de descartar.
  if (ts > ahora + 864e5) return false;
  return ahora - ts <= MAX_AGE_DAYS * 864e5;
}

/**
 * Lee ?fbclid= de la URL actual y lo persiste como _fbc.
 * Llamar una vez por pageload (GoogleAdsTag, el mismo punto que gclid y wa).
 */
export function captureFbclid() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (!fbclid || fbclid.length > 500) return;

    const value = `fb.${subdominioIndex()}.${Date.now()}.${fbclid}`;
    persist(value);

    // La cookie estándar sólo se escribe si falta o si el click es OTRO: pisarla con
    // un fbclid viejo rompería el last click que el propio pixel ya registró.
    const actual = getCookie(FBC_COOKIE);
    if (!actual || fbclidDe(actual) !== fbclid) {
      const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
      document.cookie = `${FBC_COOKIE}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`;
    }
  } catch {
    /* nunca romper la página por analytics */
  }
}

/**
 * Devuelve el _fbc vigente o null.
 * Orden: cookie _fbc del pixel (la autoritativa) -> cookie propia -> localStorage.
 * En cada paso se descarta lo vencido o con formato roto, así que un click viejo del
 * pixel no tapa uno nuestro que todavía sirve.
 */
export function getFbc() {
  const delPixel = getCookie(FBC_COOKIE);
  if (fbcVigente(delPixel)) return delPixel;

  const propia = getCookie(OWN_KEY);
  if (fbcVigente(propia)) return propia;

  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(OWN_KEY);
    if (raw) {
      const { v, t } = JSON.parse(raw);
      if (v && t && Date.now() - t < MAX_AGE_DAYS * 864e5 && fbcVigente(v)) return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}
